class BlogFacets extends HTMLElement {
  constructor() {
    super();
    this.currentRequest = null;
    this.debouncedOnSubmit = this.debounce((event) => {
      this.onSubmitHandler(event);
    }, 300);
    
    // Listen for topic filter changes
    this.addEventListener('topic-filter-change', this.onTopicFilterChange.bind(this));
    
    // Set initial state
    this.searchParamsInitial = window.location.search.slice(1);
    this.searchParamsPrev = window.location.search.slice(1);
    
    // Handle browser back/forward
    window.addEventListener('popstate', this.onHistoryChange.bind(this));
  }
  
  onTopicFilterChange(event) {
    const { selectedTopics } = event.detail;
    this.updateFilters(selectedTopics);
  }
  
  updateFilters(selectedTopics) {
    let searchParams = '';
    
    if (selectedTopics.length > 0) {
      // Build the tagged URL
      const blogHandle = this.dataset.blogHandle;
      const newPath = `/blogs/${blogHandle}/tagged/${selectedTopics.join('+')}`;
      
      // Update URL without reloading
      const newUrl = `${newPath}${window.location.search}`;
      window.history.pushState({ path: newPath, topics: selectedTopics }, '', newUrl);
    } else {
      // Clear filters
      const blogHandle = this.dataset.blogHandle;
      const newPath = `/blogs/${blogHandle}`;
      window.history.pushState({ path: newPath, topics: [] }, '', newPath);
    }
    
    this.renderPage();
  }
  
  onHistoryChange(event) {
    this.renderPage();
  }
  
  renderPage() {
    // Show loading state
    this.showLoading();
    
    // Cancel any existing request
    if (this.currentRequest) {
      this.currentRequest.abort();
    }
    
    // Create new abort controller
    const abortController = new AbortController();
    this.currentRequest = abortController;
    
    // Fetch the current URL with section parameter
    const sectionId = this.dataset.sectionId;
    const url = `${window.location.pathname}?section_id=${sectionId}`;
    
    fetch(url, { signal: abortController.signal })
      .then(response => response.text())
      .then(html => {
        this.currentRequest = null;
        this.renderResults(html);
      })
      .catch(error => {
        if (error.name !== 'AbortError') {
          console.error('Error fetching blog articles:', error);
          this.hideLoading();
        }
      });
  }
  
  renderResults(html) {
    const parser = new DOMParser();
    const newDocument = parser.parseFromString(html, 'text/html');
    
    // Update the grid
    const newGrid = newDocument.querySelector('.blog-list__grid');
    const currentGrid = this.querySelector('.blog-list__grid');
    if (newGrid && currentGrid) {
      currentGrid.innerHTML = newGrid.innerHTML;
    }
    
    // Update pagination/load more
    const newLoadMore = newDocument.querySelector('.blog-list__load-more');
    const currentLoadMore = this.querySelector('.blog-list__load-more');
    if (currentLoadMore) {
      if (newLoadMore) {
        currentLoadMore.innerHTML = newLoadMore.innerHTML;
        this.initializeLoadMore();
      } else {
        currentLoadMore.remove();
      }
    }
    
    // Update topic dropdown selections
    const newDropdown = newDocument.querySelector('.topic-dropdown');
    const currentDropdown = this.querySelector('.topic-dropdown');
    if (newDropdown && currentDropdown) {
      // Update selected states
      const newSelectedOptions = newDropdown.querySelectorAll('.topic-dropdown__option.selected');
      const currentOptions = currentDropdown.querySelectorAll('.topic-dropdown__option');
      
      currentOptions.forEach(option => {
        const isSelected = Array.from(newSelectedOptions).some(
          newOption => newOption.dataset.value === option.dataset.value
        );
        option.classList.toggle('selected', isSelected);
      });
      
      // Update button text
      this.updateDropdownButtonText();
    }
    
    // Hide loading state
    this.hideLoading();
    
    // Trigger any animations
    this.triggerAnimations();
  }
  
  showLoading() {
    const grid = this.querySelector('.blog-list__grid');
    const loadingDiv = this.querySelector('.blog-list__loading');
    
    if (grid) grid.style.opacity = '0.5';
    if (loadingDiv) loadingDiv.classList.add('active');
  }
  
  hideLoading() {
    const grid = this.querySelector('.blog-list__grid');
    const loadingDiv = this.querySelector('.blog-list__loading');
    
    if (grid) grid.style.opacity = '1';
    if (loadingDiv) loadingDiv.classList.remove('active');
  }
  
  updateDropdownButtonText() {
    const dropdown = this.querySelector('.topic-dropdown');
    if (!dropdown) return;
    
    const button = dropdown.querySelector('.topic-dropdown__button-text');
    const selectedOptions = dropdown.querySelectorAll('.topic-dropdown__option.selected');
    
    if (selectedOptions.length === 0) {
      button.textContent = dropdown.dataset.placeholder || 'VLOG CATEGORIES';
    } else if (selectedOptions.length === 1) {
      button.textContent = selectedOptions[0].dataset.text.toUpperCase();
    } else {
      button.textContent = `${selectedOptions.length} SELECTED`;
    }
  }
  
  initializeLoadMore() {
    const loadMoreBtn = this.querySelector('.blog-list__load-more-button');
    if (!loadMoreBtn) return;
    
    loadMoreBtn.addEventListener('click', async function() {
      // Re-implement load more functionality
      const currentPage = parseInt(this.dataset.page);
      const totalPages = parseInt(this.dataset.totalPages);
      const nextPage = currentPage + 1;
      
      if (nextPage > totalPages) return;
      
      this.disabled = true;
      const loadingDiv = document.querySelector('.blog-list__loading');
      if (loadingDiv) loadingDiv.classList.add('active');
      
      try {
        const url = `${window.location.pathname}?page=${nextPage}`;
        const response = await fetch(url);
        const html = await response.text();
        const parser = new DOMParser();
        const doc = parser.parseFromString(html, 'text/html');
        const newArticles = doc.querySelectorAll('.blog-list__grid .article-card-2');
        const grid = document.querySelector('.blog-list__grid');
        
        newArticles.forEach(article => {
          grid.appendChild(article.cloneNode(true));
        });
        
        this.dataset.page = nextPage;
        
        if (nextPage >= totalPages) {
          this.style.display = 'none';
        }
      } catch (error) {
        console.error('Error loading more articles:', error);
      } finally {
        this.disabled = false;
        if (loadingDiv) loadingDiv.classList.remove('active');
      }
    });
  }
  
  triggerAnimations() {
    // Fade in new articles
    const articles = this.querySelectorAll('.article-card-2');
    articles.forEach((article, index) => {
      article.style.opacity = '0';
      article.style.transform = 'translateY(20px)';
      
      setTimeout(() => {
        article.style.transition = 'opacity 0.3s ease, transform 0.3s ease';
        article.style.opacity = '1';
        article.style.transform = 'translateY(0)';
      }, index * 50);
    });
  }
  
  debounce(fn, wait) {
    let timeout;
    return function executedFunction(...args) {
      const later = () => {
        clearTimeout(timeout);
        fn(...args);
      };
      clearTimeout(timeout);
      timeout = setTimeout(later, wait);
    };
  }
}

customElements.define('blog-facets', BlogFacets);